import * as React from "react";
import { useState, useEffect } from "react";

import { Entry } from "../models/entries";
import { Comment } from "../models/comments";
import { api } from "../api";
import { User, UserJSON, Levels } from "../models/users";
import { EntryList } from "../DesignSystem/EntryList";
import { RouteComponentProps, Redirect } from "react-router";
import { TrophyJSON } from "../models/trophies";
import { CompoType } from "../models/compos";
import { useUser } from "../state";
import { LeaveAComment, commentReplyHandle } from "../Comments/LeaveAComment";
import { CommentJSON } from "../models/comments";
import { CommentComponent } from "../DesignSystem/CommentComponent";
import { RLink } from "../DesignSystem/RLink";
import { Button } from "../DesignSystem/Button";
import { SmallButton } from "../DesignSystem/SmallButton";
import { TwoColumnView, Page } from "../DesignSystem/formatting/TwoColumnView";
import { FeedPanel } from "../DesignSystem/formatting/FeedComponents";
import { EntryType } from "../Compo/CompoPage";
import {
  ChatBubbleLeftIcon,
  DocumentTextIcon,
  PlayIcon,
  HeartIcon,
  NoSymbolIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/solid";
import { ConfirmModal } from "../DesignSystem/ConfirmModal";
import { Tooltip } from "../DesignSystem/Tooltip";
import {
  ModerationActions,
  ModerationActionType,
} from "../../../src/moderation";
import { UserProfileIntro } from "./UserProfile/UserProfileInfo";

interface UserViewProps extends RouteComponentProps<{ username: string }> {}

// Clean tab component
const TabSelector = ({
  tabs,
  activeTab,
  onSelect,
}: {
  tabs: { label: string; count?: number }[];
  activeTab: number;
  onSelect: (index: number) => void;
}) => {
  return (
    <div className="mb-4 flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
      {tabs.map((tab, i) => (
        <button
          key={i}
          onClick={() => onSelect(i)}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition ${
            i === activeTab
              ? "bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white"
              : "text-slate-600 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white"
          }`}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className="ml-1.5 text-slate-400 dark:text-slate-500">
              ({tab.count})
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

// Calculate level progress
const getLevelProgress = (
  experience: number
): { level: number; progress: number; currentXp: number; totalXp: number } => {
  for (let i = 0; i < Levels.length; i++) {
    if (Levels[i] >= experience) {
      const prevLevel = i === 0 ? 0 : Levels[i - 1];
      const nextLevelXp = i + 1 < Levels.length ? Levels[i + 1] : Levels[i];
      const xpInLevel = nextLevelXp - prevLevel;
      const progress =
        xpInLevel > 0 ? ((experience - prevLevel) / xpInLevel) * 100 : 0;
      // Return total accumulated XP and total XP required for next level
      return {
        level: i,
        progress,
        currentXp: experience,
        totalXp: nextLevelXp,
      };
    }
  }
  return {
    level: 999,
    progress: 100,
    currentXp: experience,
    totalXp: experience,
  };
};

// Level progress bar component
const LevelProgressBar = ({
  label,
  experience,
  color,
}: {
  label: string;
  experience: number;
  color: string;
}) => {
  const { level, progress, currentXp, totalXp } = getLevelProgress(experience);

  const textContent = (
    <div className="flex w-full items-center justify-between px-2 text-xs font-medium">
      <span>
        Lv.{level} {label}
      </span>
      <span>
        {currentXp}/{totalXp} XP
      </span>
    </div>
  );

  return (
    <div className="relative h-6 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
      {/* Text on background (dark) */}
      <div className="absolute inset-0 flex items-center text-slate-600 dark:text-slate-400">
        {textContent}
      </div>
      {/* Filled portion with light text */}
      <div
        className={`absolute inset-y-0 left-0 overflow-hidden rounded-full transition-all ${color}`}
        style={{ width: `${progress}%` }}
      >
        <div
          className="flex h-full items-center text-white"
          style={{ width: `${10000 / Math.max(progress, 1)}%` }}
        >
          {textContent}
        </div>
      </div>
    </div>
  );
};

// User info panel for right column
const UserInfoPanel = ({
  profile_user,
  username,
  counts,
}: {
  profile_user: User;
  username: string;
  counts: {
    comments: number;
    reviews: number;
    plays: number;
    favorites: number;
  };
}) => {
  return (
    <FeedPanel title={<>About</>}>
      <div className="space-y-4">
        {/* Level Progress Bars */}
        <div className="space-y-3">
          <LevelProgressBar
            label="Musician"
            experience={profile_user.experience}
            color="bg-emerald-500"
          />
          <LevelProgressBar
            label="Artist"
            experience={profile_user.experience_art}
            color="bg-violet-500"
          />
          <LevelProgressBar
            label="Writer"
            experience={profile_user.experience_story}
            color="bg-amber-500"
          />
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200 dark:border-slate-700" />

        {/* Member since */}
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Member since {profile_user.created.toDateString()}
        </div>

        {/* Links */}
        <div className="flex flex-wrap gap-2 text-sm">
          <SmallButton
            to={`/user/${username}/comments`}
            icon={<ChatBubbleLeftIcon className="h-4 w-4" />}
          >
            {counts.comments} Comments
          </SmallButton>
          <SmallButton
            to={`/user/${username}/reviews`}
            icon={<DocumentTextIcon className="h-4 w-4" />}
          >
            {counts.reviews} Reviews
          </SmallButton>
          {profile_user.play_visibility !== "none" && (
            <SmallButton
              to={`/user/${username}/plays`}
              icon={<PlayIcon className="h-4 w-4" />}
            >
              {counts.plays} Plays
            </SmallButton>
          )}
          <SmallButton
            to={`/user/${username}/favorites`}
            icon={<HeartIcon className="h-4 w-4" />}
          >
            {counts.favorites} Favorites
          </SmallButton>
        </div>
      </div>
    </FeedPanel>
  );
};

// Trophy panel for right column
const TrophyPanel = ({
  trophies,
  entries,
}: {
  trophies: TrophyJSON[];
  entries: Entry[];
}) => {
  const entryTrophies: { trophy: TrophyJSON; entry: Entry }[] = [];
  for (const trophy of trophies) {
    const entry = entries.find((e) => e.id === trophy.entry_id);
    if (entry) {
      entryTrophies.push({ trophy, entry });
    }
  }

  const hasTrophiesOfType: { [type in EntryType]: boolean } = {
    song: !!entryTrophies.find(({ entry }) => entry.type === "song"),
    art: !!entryTrophies.find(({ entry }) => entry.type === "art"),
    story: !!entryTrophies.find(({ entry }) => entry.type === "story"),
  };

  if (entryTrophies.length === 0) {
    return null;
  }

  const renderTrophyList = (type: EntryType) => {
    const filtered = entryTrophies.filter((x) => x.entry.type === type);
    if (filtered.length === 0) return null;

    return (
      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {type} trophies
        </div>
        <div className="flex flex-wrap gap-1">
          {filtered.map(({ trophy, entry }) => (
            <Tooltip
              key={trophy.id}
              tooltip={`${entry.title}: ${trophy.trophy_description}`}
              position="top"
              element={
                <RLink to={`/entry/${trophy.entry_id}`}>
                  <img
                    src={trophy.trophy_url}
                    className="h-10 w-10 transition hover:scale-110"
                    alt={trophy.trophy_description}
                  />
                </RLink>
              }
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <FeedPanel title={<>Trophies</>}>
      <div className="space-y-4">
        {(["song", "story", "art"] as EntryType[]).map(
          (type) => hasTrophiesOfType[type] && renderTrophyList(type)
        )}
      </div>
    </FeedPanel>
  );
};

// Admin panel for moderators
const AdminPanel = ({
  isBanned,
  onBanClick,
  onUnbanClick,
  onViewHistoryClick,
}: {
  isBanned: boolean;
  onBanClick: () => void;
  onUnbanClick: () => void;
  onViewHistoryClick: () => void;
}) => {
  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/90 p-4 shadow-sm backdrop-blur-sm dark:border-violet-400/20 dark:bg-violet-500/10 dark:shadow-none">
      <h2 className="mb-3 text-xl font-semibold text-violet-700 dark:text-violet-300">
        Secret Admin Panel
      </h2>
      <div className="flex flex-col gap-3">
        {isBanned ? (
          <Button
            onClick={onUnbanClick}
            variant="success"
            icon={<NoSymbolIcon className="h-4 w-4" />}
          >
            Unban this user
          </Button>
        ) : (
          <Button
            onClick={onBanClick}
            variant="admin"
            icon={<NoSymbolIcon className="h-4 w-4" />}
          >
            Ban this user
          </Button>
        )}
        <Button
          onClick={onViewHistoryClick}
          icon={<ClipboardDocumentListIcon className="h-4 w-4" />}
        >
          See all moderation actions
        </Button>
      </div>
    </div>
  );
};

// User header component
const UserHeader = ({ profile_user }: { profile_user: User }) => {
  return (
    <div className="mb-6 flex items-start gap-4">
      <img
        src={profile_user.avatar_url}
        className="h-20 w-20 flex-shrink-0 object-cover shadow-md"
        alt={profile_user.username}
      />
      <div className="min-w-0 flex-1">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          {profile_user.username}
        </h1>
        <UserProfileIntro profile_user={profile_user} />
      </div>
    </div>
  );
};

export const UserView = (props: UserViewProps) => {
  const [profile_user, setProfileUser] = useState<User | undefined>(undefined);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [trophies, setTrophies] = useState<TrophyJSON[]>([]);
  const [associatedCompos, setAssociatedCompos] = useState<
    { compo_id: number; name: string; type: CompoType }[]
  >([]);
  const [avatarUrls, setAvatarUrls] = useState<string[][]>([]);
  const [comments, setComments] = useState<
    { comment: CommentJSON; user: UserJSON; likes: number; is_liked: boolean }[]
  >([]);
  const [counts, setCounts] = useState<{
    comments: number;
    reviews: number;
    plays: number;
    favorites: number;
  }>({ comments: 0, reviews: 0, plays: 0, favorites: 0 });
  const [failed, setFailed] = useState(false);
  const userState = useUser();
  const user = userState.type === "logged-in" ? userState.user : undefined;
  const [activeTab, setActiveTab] = useState(
    window.location.hash === "#comments" ? 1 : 0
  );
  const [showBanModal, setShowBanModal] = useState(false);
  const [showUnbanModal, setShowUnbanModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [unbanReason, setUnbanReason] = useState("");
  const [moderationHistory, setModerationHistory] = useState<
    {
      id: string;
      createdAt: string;
      actionType: ModerationActionType;
      actorUsername: string;
      reason: string | null;
      content: string | null;
    }[]
  >([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const isAdmin = userState.type === "logged-in" && userState.user.admin;

  useEffect(() => {
    loadUserData();
  }, [props.match.params.username]);

  const loadUserData = async () => {
    const results = await api.get<"/api/user/:username">(
      `/api/user/${props.match.params.username}`
    );

    if (api.isToast(results)) {
      setFailed(true);
      return;
    }

    setProfileUser(new User(results.user));
    setComments(results.comments);
    setEntries(results.entries.map((x) => new Entry(x)));
    setTrophies(results.trophies);
    setAssociatedCompos(results.associatedCompos);
    setAvatarUrls(results.avatarUrls);
    setCounts(results.counts);
  };

  const handleBan = async () => {
    if (!profile_user) return;

    const result = await api.post<"/api/admin/ban">("/api/admin/ban", {
      user_id: profile_user.id,
      reason: banReason,
    });

    if (api.isToast(result)) {
      return;
    }

    setShowBanModal(false);
    setBanReason("");
    loadUserData();
  };

  const handleUnban = async () => {
    if (!profile_user) return;

    const result = await api.post<"/api/admin/unban">("/api/admin/unban", {
      user_id: profile_user.id,
      reason: unbanReason,
    });

    if (api.isToast(result)) {
      return;
    }

    setShowUnbanModal(false);
    setUnbanReason("");
    loadUserData();
  };

  const handleViewHistory = async () => {
    if (!profile_user) return;

    setShowHistoryModal(true);
    setHistoryLoading(true);

    const result = await api.get<"/api/admin/user/:id/moderation-history">(
      `/api/admin/user/${profile_user.id}/moderation-history`
    );

    if (api.isToast(result)) {
      setHistoryLoading(false);
      return;
    }

    setModerationHistory(result.events);
    setHistoryLoading(false);
  };

  const formatActionType = (actionType: ModerationActionType): string => {
    return ModerationActions[actionType];
  };

  if (failed) {
    return <Redirect to="/feed" />;
  }

  if (!profile_user) {
    return (
      <Page>
        <div className="py-20 text-center text-slate-500">Loading...</div>
      </Page>
    );
  }

  const username = props.match.params.username;

  const tabs = [
    { label: "Entries", count: entries.length },
    { label: "Comments", count: comments.length },
  ];

  return (
    <Page>
      <TwoColumnView
        left={
          <>
            <UserHeader profile_user={profile_user} />

            <TabSelector
              tabs={tabs}
              activeTab={activeTab}
              onSelect={setActiveTab}
            />

            {activeTab === 0 && (
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-800">
                {entries.length > 0 ? (
                  <EntryList
                    hideStats
                    entries={entries}
                    associatedCompos={associatedCompos}
                    trophies={trophies}
                    avatar_urls={avatarUrls}
                  />
                ) : (
                  <div className="py-8 text-center text-slate-500 dark:text-slate-400">
                    No entries yet
                  </div>
                )}
              </div>
            )}

            {activeTab === 1 && (
              <div className="space-y-3">
                {comments.length === 0 && (
                  <div className="rounded-2xl border border-slate-100 bg-white p-4 py-8 text-center text-slate-500 shadow-sm dark:border-white/10 dark:bg-slate-800 dark:text-slate-400">
                    No comments yet. Be the first to say hello!
                  </div>
                )}
                {comments.map((comment) => (
                  <CommentComponent
                    key={comment.comment.id}
                    anonymous="never"
                    replyCB={(replyUsername) =>
                      commentReplyHandle.replyTo(replyUsername)
                    }
                    comment_user={new User(comment.user)}
                    comment_likes={comment.likes}
                    comment_is_liked={comment.is_liked}
                    comment={new Comment(comment.comment)}
                  />
                ))}

                <LeaveAComment
                  canPostAnonymously={false}
                  user={profile_user}
                  onAddComment={(comment) => {
                    setComments((old) => [
                      ...old,
                      {
                        comment: comment,
                        user: user!,
                        likes: 0,
                        is_liked: false,
                      },
                    ]);
                  }}
                />
              </div>
            )}
          </>
        }
        right={
          <>
            {isAdmin && (
              <AdminPanel
                isBanned={profile_user.banned}
                onBanClick={() => setShowBanModal(true)}
                onUnbanClick={() => setShowUnbanModal(true)}
                onViewHistoryClick={handleViewHistory}
              />
            )}
            <UserInfoPanel
              profile_user={profile_user}
              username={username}
              counts={counts}
            />
            <TrophyPanel trophies={trophies} entries={entries} />
          </>
        }
      />

      <ConfirmModal
        isOpen={showBanModal}
        onClose={() => {
          setShowBanModal(false);
          setBanReason("");
        }}
        onConfirm={handleBan}
        title="Are you sure you want to ban this user?"
        confirmText="Ban User"
        confirmVariant="danger"
      >
        <p className="mb-3">
          This is the nuclear option. I assume either you've tried to talk it
          out over Discord and failed, or the user is massively over the line.
        </p>
        <ul className="mb-3 list-disc space-y-1 pl-5">
          <li>This user will not be able to log in again</li>
          <li>All comments and forum posts from this user will be hidden</li>
          <li>All entries will be hidden</li>
          <li>All likes will be erased, all tags will be removed</li>
          <li>This action is reversible</li>
        </ul>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Reason
          </label>
          <textarea
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            placeholder="Why are you banning this user?"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-500"
            rows={3}
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        isOpen={showUnbanModal}
        onClose={() => {
          setShowUnbanModal(false);
          setUnbanReason("");
        }}
        onConfirm={handleUnban}
        title="Are you sure you want to unban this user?"
        confirmText="Unban User"
        confirmVariant="primary"
      >
        <p className="mb-3">
          This will restore the user's ability to log in and participate.
        </p>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Reason
          </label>
          <textarea
            value={unbanReason}
            onChange={(e) => setUnbanReason(e.target.value)}
            placeholder="Why are you unbanning this user?"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-500"
            rows={3}
          />
        </div>
      </ConfirmModal>

      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowHistoryModal(false)}
          />
          <div className="relative mx-4 max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl dark:bg-slate-800">
            <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                Moderation History
              </h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-4">
              {historyLoading ? (
                <div className="py-8 text-center text-slate-500">
                  Loading...
                </div>
              ) : moderationHistory.length === 0 ? (
                <div className="py-8 text-center text-slate-500 dark:text-slate-400">
                  No moderation actions for this user
                </div>
              ) : (
                <div className="space-y-4">
                  {moderationHistory.map((event) => (
                    <div
                      key={event.id}
                      className="rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900 dark:text-white">
                              {formatActionType(event.actionType)}
                            </span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              by {event.actorUsername}
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            {new Date(event.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      {event.reason && (
                        <div className="mt-2 rounded bg-slate-100 p-2 text-sm text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                          <span className="font-medium">Reason:</span>{" "}
                          {event.reason}
                        </div>
                      )}
                      {event.content && (
                        <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-sm text-slate-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400">
                          <span className="font-medium">Content:</span>{" "}
                          {event.content.length > 200
                            ? event.content.slice(0, 200) + "..."
                            : event.content}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};
